import { invariant, keyBy, raise } from "@banjoanton/utils";
import { type ChessGame, type ChessPosition, Move } from "../index";
import { createLogger } from "@chesspresso/common";
import { Chess } from "chess.js";

const logger = createLogger("replay-engine");

type NavigationType = "step" | "jump";

export type OnPositionChangeCallback = (
    engine: ReplayEngine,
    fen: string,
    navigationType: NavigationType
) => void;

type ReplayEngineProps = {
    game: ChessGame;
    engine: Chess;
};

export class ReplayEngine {
    private game: ChessGame;
    private engine: Chess;
    private currentPosition: ChessPosition;
    private maxPosition: number;
    private currentFen: string;
    private moveMap: Record<string, Move>;
    private onPositionChangeCallbacks: OnPositionChangeCallback[] = [];

    constructor({ game, engine }: ReplayEngineProps) {
        this.game = game;
        this.engine = engine;
        this.maxPosition = game.positions.length;
        this.currentFen = game.initialFen;
        this.currentPosition =
            game.positions.at(0) ?? raise("No positions in game");

        this.moveMap = keyBy(game.moves, (move) => move.id);
        logger.debug(
            `Initialized replay engine with ${this.maxPosition} positions.`
        );
    }

    private onPositionChange(navigationType: NavigationType) {
        for (const callback of this.onPositionChangeCallbacks) {
            callback(this, this.currentFen, navigationType);
        }
    }

    private loadCurrentPosition(navigationType: NavigationType = "step") {
        const currentPosition = this.currentPosition;
        this.currentFen = currentPosition.fen;
        this.engine.load(this.currentFen);
        this.onPositionChange(navigationType);
    }

    getMoveForPosition(position: ChessPosition = this.currentPosition) {
        const previousMove = position.previousMove;

        if (!previousMove) {
            return undefined;
        }

        return this.moveMap[previousMove];
    }

    addOnPositionChangeListener(callback: OnPositionChangeCallback) {
        this.onPositionChangeCallbacks.push(callback);
    }

    removeOnPositionChangeListener(callback: OnPositionChangeCallback) {
        this.onPositionChangeCallbacks = this.onPositionChangeCallbacks.filter(
            (cb) => cb !== callback
        );
    }

    goToFirstPosition() {
        const position = this.game.positions[0];
        invariant(position, "No positions in game");
        this.currentPosition = position;
        this.engine.load(position.fen);
        this.currentFen = position.fen;
        this.onPositionChange("jump");
    }

    goToLastPosition() {
        const position = this.game.positions.at(-1);
        invariant(position, "No positions in game");
        this.currentPosition = position;
        this.engine.load(position.fen);
        this.currentFen = position.fen;
        this.onPositionChange("jump");
    }

    goToNextPosition() {
        if (this.currentPosition.index + 1 >= this.maxPosition) {
            return;
        }
        const nextPosition =
            this.game.positions[this.currentPosition.index + 1];
        invariant(nextPosition, "No next position found");
        this.currentPosition = nextPosition;
        this.loadCurrentPosition();
    }

    goToPreviousPosition() {
        if (this.currentPosition.index <= 0) {
            return;
        }
        const previousPosition =
            this.game.positions[this.currentPosition.index - 1];
        invariant(previousPosition, "No previous position found");
        this.currentPosition = previousPosition;
        this.loadCurrentPosition();
    }

    goToPosition(position: number) {
        if (position < 0 || position >= this.maxPosition) {
            throw new Error("Position out of bounds for go to.");
        }
        const pos = this.game.positions[position];
        invariant(pos, "No position found at index");
        this.currentPosition = pos;
        this.loadCurrentPosition("jump");
    }

    canGoToFirstPosition() {
        return this.currentPosition.index > 0;
    }

    canGoToLastPosition() {
        return this.currentPosition.index < this.maxPosition - 1;
    }

    canGoToNextPosition() {
        return this.currentPosition.index < this.maxPosition - 1;
    }

    canGoToPreviousPosition() {
        return this.currentPosition.index > 0;
    }

    getPositionByIndex(index: number) {
        if (index < 0 || index >= this.game.positions.length) {
            throw new Error("Index out of bounds.");
        }

        const position = this.game.positions[index];

        if (!position) {
            throw new Error(`No position found at index ${index}.`);
        }

        return position;
    }

    getMoveByIndex(index: number): Move {
        if (index < 0 || index >= this.game.moves.length) {
            throw new Error("Index out of bounds.");
        }

        const move = this.game.moves[index];

        if (!move) {
            throw new Error(`No move found at index ${index}.`);
        }

        return move;
    }

    getCurrentFen() {
        return this.currentFen;
    }

    getCurrentPosition() {
        return this.currentPosition;
    }

    isFinalPosition() {
        return this.currentPosition.index === this.maxPosition - 1;
    }

    isFirstPosition = () => this.currentPosition.index === 0;

    getGame() {
        return this.game;
    }

    getSurroundingPositions = (index: number) => {
        const previousPosition =
            index >= 1 ? this.getPositionByIndex(index - 1) : undefined;
        const positionBeforeOpponentsMove =
            index >= 2 ? this.getPositionByIndex(index - 2) : undefined;
        const nextPosition =
            index + 1 < this.getGame().positions.length
                ? this.getPositionByIndex(index + 1)
                : undefined;
        return {
            previousPosition,
            positionBeforeOpponentsMove,
            nextPosition,
        };
    };
}
